/**
 * <Checkbox> — Radix-based checkbox with primary-filled checked state.
 *
 * Wraps `@radix-ui/react-checkbox`. UI-SPEC §Color rule 9:
 *   - Unchecked:     border-border-strong on surface-elevated
 *   - Checked:       bg-primary fill + text-on-primary checkmark
 *   - Indeterminate: bg-primary-soft + primary-colored dash
 *
 * Consumers pair the checkbox with an adjacent `<label htmlFor>` (or
 * `<Form.Field>` adapter in 04c). For icon-only usage, consumers pass
 * `aria-label` through — Radix forwards all `aria-*` attrs to the root button.
 */
import * as React from "react";
import * as RadixCheckbox from "@radix-ui/react-checkbox";
import { Check, Minus } from "lucide-react";
import { cn } from "../../utils";
import { Icon } from "../Icon";

export type CheckboxProps = React.ComponentPropsWithoutRef<typeof RadixCheckbox.Root>;

export const Checkbox = React.forwardRef<
  React.ElementRef<typeof RadixCheckbox.Root>,
  CheckboxProps
>(function Checkbox({ className, checked, ...rest }, ref) {
  return (
    <RadixCheckbox.Root
      ref={ref}
      {...(checked !== undefined ? { checked } : {})}
      className={cn(
        // Shape — 16px default; touch target covered by Form.Field adapter.
        "inline-flex h-4 w-4 shrink-0 items-center justify-center",
        "rounded-sm border border-border-strong bg-surface-elevated",
        // Focus + disabled
        "focus-visible:focus-ring",
        "disabled:opacity-50 disabled:pointer-events-none",
        // Checked — primary fill (UI-SPEC §Color rule 9)
        "data-[state=checked]:bg-primary data-[state=checked]:border-primary data-[state=checked]:text-on-primary",
        // Indeterminate — primary-soft tint
        "data-[state=indeterminate]:bg-primary-soft data-[state=indeterminate]:border-primary data-[state=indeterminate]:text-primary-ink",
        "transition-colors duration-fast ease-out",
        className,
      )}
      {...rest}
    >
      <RadixCheckbox.Indicator asChild>
        <span className="inline-flex items-center justify-center">
          <Icon
            as={checked === "indeterminate" ? Minus : Check}
            name={checked === "indeterminate" ? "minus" : "check"}
            size={12}
          />
        </span>
      </RadixCheckbox.Indicator>
    </RadixCheckbox.Root>
  );
});
